import { range } from 'lodash'

import { ITableViewData } from '@/services/data-service'

import { applyNumberFormatting } from '@/components/canvas'

type HotData = (string | number)[][]

const _toSequence = (
  row1: number,
  col1: number,
  row2: number,
  col2: number
): [number[], number[]] => {
  let rowsRange = row2 > row1 ? range(row1, row2 + 1) : range(row2, row1 + 1)
  const colsRange = col2 > col1 ? range(col1, col2 + 1) : range(col2, col1 + 1)
  return [rowsRange, colsRange]
}

export const mergeSelections = (
  from: [number, number],
  to: [number, number],
  rows: number[] = [],
  columns: number[] = []
): [number[], number[]] => {
  const [selectedRows, selectedColumns] = _toSequence(...from, ...to)
  const rowSet = new Set([...selectedRows, ...rows])
  const columnSet = new Set([...selectedColumns, ...columns])
  const sortedRows = Array.from(rowSet).sort((a, b) => a - b)
  const sortedCols = Array.from(columnSet).sort((a, b) => a - b)
  return [sortedRows, sortedCols]
}

export function sequenceToRange(ids: number[]) {
  let ret = []
  let temp = []
  let i = 0
  do {
    temp.push(ids[i])
    if (ids[i] + 1 !== ids[i + 1]) {
      if (temp.length === 1) {
        ret.push([temp[0], temp[0]])
      } else {
        ret.push([temp[0], temp[temp.length - 1]])
      }
      temp = []
    }

    i++
  } while (i < ids.length)
  return ret
}

export function toRanges(rows: number[], columns: number[]) {
  const rowsRanges = sequenceToRange(rows)
  const colRanges = sequenceToRange(columns)
  let combinations = []
  for (const rowRange of rowsRanges) {
    for (const colRange of colRanges) {
      combinations.push([rowRange[0], colRange[0], rowRange[1], colRange[1]])
    }
  }
  return combinations
}

export function getColHeaders(tableDataFrame: ITableViewData) {
  if (tableDataFrame == null || !tableDataFrame?.headers) {
    return null
  }
  return tableDataFrame.headers.map(header => {
    return header.map(cell => {
      return { label: cell.name.substring(0, 40), colspan: cell.span }
    })
  })
}

export function convertData(tableDataFrame: ITableViewData): HotData {
  if (tableDataFrame == null) {
    return null
  }
  let rows = tableDataFrame.index
  let values = tableDataFrame.data.map((row, rowIndex) =>
    row.map((value, colIndex) => {
      const formatCode = tableDataFrame.tableMeta?.numberFormat?.[rowIndex]?.[colIndex]
      if (formatCode) {
        return applyNumberFormatting(value, formatCode)
      }
      return value
    })
  )
  let ret = []
  if (rows) {
    rows.forEach((_row, idx) => ret.push([...values[idx]]))
  }
  return ret
}

// Materialized nets (issue #701) are real trailing rows/columns in the DataTable. Remove them
// from the Input table display so they are not visible, while they remain available in the
// transformation dropdowns (which read the separate labels endpoint). Because nets always
// append AFTER the base data, dropping them leaves base positions - and thus selection index
// math - unchanged.
export function stripComputedMembers(
  data: HotData,
  rowHeaders: string[],
  colHeaders: { name: string; span: number }[][],
  computedRows: number[] = [],
  computedColumns: number[] = []
) {
  const rowSet = new Set(computedRows)
  const colSet = new Set(computedColumns)
  const filteredRowHeaders = rowHeaders.filter((_, i) => !rowSet.has(i))
  const filteredData = data
    .filter((_, i) => !rowSet.has(i))
    .map(row => row.filter((_, j) => !colSet.has(j)))
  // Each computed column appended a trailing span-1 cell at every header level, so dropping
  // the last N cells per level removes exactly those columns.
  const drop = computedColumns.length
  const filteredColHeaders = drop
    ? colHeaders.map(level => level.slice(0, level.length - drop))
    : colHeaders
  return { data: filteredData, rowHeaders: filteredRowHeaders, colHeaders: filteredColHeaders }
}

export function getSelectionRange(
  hotTableSelectedArea: number[][] | undefined,
  rowsCount: number,
  colsCount: number
) {
  const resultRangeCols = []
  const resultRangeRows = []

  const selectedColumns = hotTableSelectedArea
    ?.map(range => {
      return range.filter((_, idx) => {
        return idx === 1
      })
    })
    .flat()

  selectedColumns.forEach(colIndex => {
    const arr = []
    arr[0] = 0
    arr[1] = colIndex
    arr[2] = rowsCount
    arr[3] = colIndex
    resultRangeCols.push(arr)
  })

  const selectedRows = hotTableSelectedArea
    ?.map(range => {
      return range.filter((_, idx) => {
        return idx === 0
      })
    })
    .flat()

  selectedRows.forEach(rowIndex => {
    const arr = []
    arr[0] = rowIndex
    arr[1] = 0
    arr[2] = rowIndex
    arr[3] = colsCount
    resultRangeRows.push(arr)
  })

  return { resultRangeCols, resultRangeRows, selectedColumns, selectedRows }
}

export function getGlobalNumberFormat(
  numberFormat: string[][],
  rows: number[],
  columns: number[]
): string | undefined {
  if (!numberFormat) {
    return
  }
  let formatCode = ''
  let isConsistent = true
  rows.forEach(row => {
    if (!isConsistent) {
      return
    }
    columns.forEach(col => {
      let currentCode = numberFormat?.[row]?.[col]
      // @ symbol is used for cells that have no values, we ignore them
      if (currentCode !== '@') {
        // if formatCode changes across the data set we ignore it
        if (formatCode && currentCode !== formatCode) {
          isConsistent = false
          return
        }
        formatCode = currentCode
      }
    })
  })
  return isConsistent ? formatCode : null
}

const PLAIN_NUMBER = /^(-?)(0|[1-9]\d*)(\.\d+)?$/
const NUMERIC = /^[-+]?\d[\d,]*(\.\d+)?%?$/

export function isNumeric(value: string | number) {
  return NUMERIC.test(String(value ?? '').trim())
}

export function withThousands(value: string | number) {
  const match = String(value ?? '').match(PLAIN_NUMBER)
  if (!match) {
    return value
  }
  const [, sign, whole, fraction = ''] = match
  return `${sign}${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${fraction}`
}
